// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setRequisitionIsExpanded from "@insite/client-framework/Store/Pages/Requisitions/Handlers/SetRequisitionIsExpanded";
import setRequisitionIsSelected from "@insite/client-framework/Store/Pages/Requisitions/Handlers/SetRequisitionIsSelected";
import translate from "@insite/client-framework/Translate";
import { RequisitionModel } from "@insite/client-framework/Types/ApiModels";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import RequisitionsRequisitionLinesTable from "@insite/content-library/Widgets/Requisitions/RequisitionsRequisitionLinesTable";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartLineContainer, { CartLineContainerStyles } from "@sc/App/Components/Cart/CartLineContainer";
import QtyField from "@sc/App/Components/Qty/QtyField";
import { hidePrice } from "@sc/App/Store/Selectors/GeneralSelectors";
import ProductBrand from "@sc/Overrides/Components/ProductBrand";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    requisition: RequisitionModel;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isSelected: !!state.pages.requisitions.selectedRequisitionIds.find(o => o === ownProps.requisition.id),
    isExpanded: !!state.pages.requisitions.expandedRequisitionIds.find(o => o === ownProps.requisition.id),
    productSettings: getSettingsCollection(state).productSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    // XNG-Change
    hidePrices: hidePrice(state),
});

const mapDispatchToProps = {
    setRequisitionIsSelected,
    setRequisitionIsExpanded,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RequisitionsProductListLineStyles {
    mainContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    selectedCheckbox?: CheckboxPresentationProps;
    imageWrapper?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    infoGridItem?: GridItemProps;
    infoContainer?: GridContainerProps;
    descriptionGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    partNumbersGridItem?: GridItemProps;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    quantityGridItem?: GridItemProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    subtotalGridItem?: GridItemProps;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    subtotalHeadingAndText?: SmallHeadingAndTextStyles;
    requisitionLinesGridItem?: GridItemProps;
    requisitionLinesAccordion?: AccordionPresentationProps;
    // XNG-Change
    GridContainer?: CartLineContainerStyles;
    noPrice?: SmallHeadingAndTextStyles;
}

export const requisitionsProductListLineStyles: RequisitionsProductListLineStyles = {
    mainContainer: {
        gap: 10,
        css: css`
            padding: 20px 0;
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
    imageGridItem: {
        width: [3, 3, 2, 2, 2],
    },
    selectedCheckbox: {
        css: css`
            margin-right: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            min-width: 0;
            flex-direction: column;
        `,
    },
    infoGridItem: {
        width: [9, 9, 10, 10, 10],
    },
    infoContainer: {
        gap: 10,
    },
    descriptionGridItem: {
        width: [12, 12, 5, 6, 6],
        printWidth: 6,
        css: css`
            flex-direction: column;
        `,
    },
    partNumbersGridItem: { width: 12 },
    productPriceStyles: {
        priceLabelText: {
            variant: "p",
            transform: "uppercase",
            size: 10,
            weight: "bold",
            css: css`
                margin-bottom: 0.5rem;
            `,
        },
    },
    priceGridItem: { width: [4, 4, 2, 2, 2] },
    quantityGridItem: { width: [4, 4, 2, 2, 2] },
    quantityHeadingAndText: {
        heading: {
            size: 10,
            weight: "bold",
            css: css`
                margin-bottom: 0.5rem;
            `,
        },
    },
    subtotalGridItem: {
        width: [4, 4, 2, 2, 2],
        css: css`
            flex-direction: column;
        `,
    },
    subtotalWithoutVatHeadingAndText: {
        heading: {
            size: 10,
            weight: "bold",
            css: css`
                margin-bottom: 0.5rem;
            `,
        },
        text: {
            weight: "bold",
        },
        wrapper: {
            css: css`
                margin-bottom: 10px;
            `,
        },
    },
    subtotalHeadingAndText: {
        heading: {
            size: 10,
            weight: "bold",
            css: css`
                margin-bottom: 0.5rem;
            `,
        },
        text: {
            weight: "bold",
        },
    },
    requisitionLinesGridItem: {
        width: 12,
    },
    requisitionLinesAccordion: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = requisitionsProductListLineStyles;

const RequisitionsProductListLine = ({
    requisition,
    isSelected,
    isExpanded,
    productSettings,
    enableVat,
    vatPriceDisplay,
    showPricing,
    setRequisitionIsSelected,
    setRequisitionIsExpanded,
    // XNG-Change
    hidePrices,
}: Props) => {
    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setRequisitionIsSelected({ requisitionId: requisition.id, isSelected: value });
    };

    const togglePanelHandler = () => {
        setRequisitionIsExpanded({ requisitionId: requisition.id, isExpanded: !isExpanded });
    };

    // XNG-Changes: Structure
    return (
        <CartLineContainer
            extendedStyles={styles.GridContainer}
            data-test-selector={`requisition_${requisition.productId}_${requisition.unitOfMeasure}`}
        >
            <CartLineContainer.Image>
                <ProductImage product={requisition} extendedStyles={styles.productImageStyles} />
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                {requisition.brand && (
                    <ProductBrand
                        brand={requisition.brand}
                        extendedStyles={styles.productBrandStyles}
                        productDetailPagePath={requisition.productUri}
                    />
                )}
                <ProductDescription product={requisition} extendedStyles={styles.productDescriptionStyles} />

                <ProductPartNumbers
                    productNumber={requisition.erpNumber}
                    customerProductNumber={requisition.customerName}
                    manufacturerItem={requisition.manufacturerItem}
                    extendedStyles={styles.productPartNumbersStyles}
                />
                <Checkbox {...styles.selectedCheckbox} checked={isSelected} onChange={selectChangeHandler}>
                    {translate("Select")}
                </Checkbox>
            </CartLineContainer.Description>
            <CartLineContainer.Price>
                <ProductPrice
                    product={requisition}
                    showSavings={true}
                    showSavingsAmount={productSettings.showSavingsAmount}
                    showSavingsPercent={productSettings.showSavingsPercent}
                    extendedStyles={styles.productPriceStyles}
                    // Xng-Changes
                    showLabel={true}
                    label={translate("Price/Unit")}
                />
            </CartLineContainer.Price>
            <CartLineContainer.Qty>
                <QtyField editable={false} value={requisition.qtyOrdered!} />
            </CartLineContainer.Qty>
            <CartLineContainer.Total>
                {requisition.pricing && showPricing && (
                    <>
                        {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                            <SmallHeadingAndText
                                heading={`${translate("Total")} (${translate("Ex. VAT")})`}
                                text={requisition.pricing.extendedUnitNetPriceDisplay}
                                extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                            />
                        )}
                        <SmallHeadingAndText
                            // XNG-Change
                            {...(hidePrices ? { ...styles.noPrice } : { ...styles.subtotalHeadingAndText })}
                            heading={
                                !enableVat
                                    ? translate("Total")
                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                    ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                    : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                            }
                            text={
                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? requisition.pricing.extendedUnitRegularPriceWithVatDisplay
                                    : requisition.pricing.extendedUnitNetPriceDisplay
                            }
                            data-test-selector="subtotal"
                        />
                    </>
                )}
            </CartLineContainer.Total>
            <CartLineContainer.Notes>
                <Accordion {...styles.requisitionLinesAccordion} headingLevel={3}>
                    <AccordionSection
                        title={translate("View Requisitions")}
                        expanded={isExpanded}
                        onTogglePanel={togglePanelHandler}
                    >
                        {isExpanded && <RequisitionsRequisitionLinesTable requisitionId={requisition.id} />}
                    </AccordionSection>
                </Accordion>
            </CartLineContainer.Notes>
        </CartLineContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RequisitionsProductListLine);
